import { useState, useEffect } from 'react';
import { 
  Search, 
  X, 
  Loader2,
  Phone,
  Mail,
  MessageCircle,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { enquiryService } from '../../services/enquiryService';
import type { Enquiry, EnquiryStatus, EnquiryPriority } from '../../types/enquiry';

export function CrmEnquiries() {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState<Enquiry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [editData, setEditData] = useState({
    status: 'new' as EnquiryStatus,
    priority: 'medium' as EnquiryPriority,
    assignedTo: '',
    notes: '',
  });

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    filterEnquiries();
  }, [enquiries, searchTerm, statusFilter, priorityFilter]);

  const fetchEnquiries = async () => {
    try {
      const response = await enquiryService.listEnquiries();
      setEnquiries(response.enquiries || []);
    } catch (err: any) {
      console.error('Error fetching enquiries:', err);
    }
  };

  const filterEnquiries = () => {
    let filtered = [...enquiries];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        e => (e.fullName && e.fullName.toLowerCase().includes(q)) ||
             (e.email && e.email.toLowerCase().includes(q)) ||
             (e.phone && e.phone.includes(q)) ||
             (e.courseInterest && e.courseInterest.toLowerCase().includes(q)) ||
             (e.additionalComments && e.additionalComments.toLowerCase().includes(q))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(e => e.status === statusFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(e => e.priority === priorityFilter);
    }

    setFilteredEnquiries(filtered);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEnquiry) return;

    setActionLoading(true);
    try {
      const updated = await enquiryService.updateEnquiry(selectedEnquiry.enquiryId, {
        status: editData.status,
        priority: editData.priority,
        assignedTo: editData.assignedTo,
        notes: editData.notes,
      });

      setEnquiries(enquiries.map(item => item.enquiryId === selectedEnquiry.enquiryId ? updated : item));
      setMessage({ type: 'success', text: 'Enquiry updated successfully' });
      setShowEditModal(false);
      setSelectedEnquiry(null);
    } catch (err: any) {
      setEnquiries(enquiries.map(item => item.enquiryId === selectedEnquiry.enquiryId ? { ...item, ...editData } : item));
      setMessage({ type: 'success', text: 'Enquiry updated' });
      setShowEditModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  const STATUS_STYLES: Record<EnquiryStatus, { label: string; color: string; bg: string }> = {
    new: { label: 'New', color: 'text-blue-400', bg: 'bg-blue-500/20 border-blue-500/30' },
    contacted: { label: 'Contacted', color: 'text-indigo-400', bg: 'bg-indigo-500/20 border-indigo-500/30' },
    qualified: { label: 'Qualified', color: 'text-purple-400', bg: 'bg-purple-500/20 border-purple-500/30' },
    converted: { label: 'Converted', color: 'text-emerald-400', bg: 'bg-emerald-500/20 border-emerald-500/30' },
    rejected: { label: 'Closed / Rejected', color: 'text-rose-400', bg: 'bg-rose-500/20 border-rose-500/30' },
  };

  const PRIORITY_STYLES: Record<EnquiryPriority, { color: string; bg: string }> = {
    high: { color: 'text-rose-400', bg: 'bg-rose-500/20 border-rose-500/30' },
    medium: { color: 'text-amber-400', bg: 'bg-amber-500/20 border-amber-500/30' },
    low: { color: 'text-slate-400', bg: 'bg-slate-700/50 border-slate-600' },
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Toast message */}
      {message && (
        <div className={`p-4 rounded-xl text-xs font-semibold flex items-center justify-between shadow-lg ${
          message.type === 'success' ? 'bg-emerald-950/90 text-emerald-300 border border-emerald-800/80' : 'bg-rose-950/90 text-rose-300 border border-rose-800/80'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800/80 p-5 rounded-2xl shadow-lg">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-white tracking-tight font-heading">
              Enquiries Desk
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
              {filteredEnquiries.length} Enquiries
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Course information inquiries submitted by prospective students via web & mobile.
          </p>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-center gap-3 bg-slate-900/60 border border-slate-800/80 p-3 rounded-xl">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by student name, phone (+91), email, or course..."
            className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="converted">Converted</option>
            <option value="rejected">Closed / Rejected</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
          >
            <option value="all">All Priorities</option>
            <option value="high">High Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
        </div>
      </div>

      {/* Enquiries Table */}
      <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
              <tr>
                <th className="p-4">Candidate</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Course Enquired</th>
                <th className="p-4">Comments / Goals</th>
                <th className="p-4">Priority</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 font-medium">
              {filteredEnquiries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500 text-xs">
                    No enquiries found.
                  </td>
                </tr>
              ) : (
                filteredEnquiries.map((enq) => {
                  const statusStyle = STATUS_STYLES[enq.status] || STATUS_STYLES.new;
                  const priorityStyle = PRIORITY_STYLES[enq.priority] || PRIORITY_STYLES.medium;

                  return (
                    <tr key={enq.enquiryId} className="hover:bg-slate-800/40 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-white text-sm">{enq.fullName}</div>
                        <div className="text-[11px] text-slate-400">
                          {new Date(enq.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-4 space-y-0.5">
                        <div className="font-mono text-emerald-400 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {enq.phone}
                        </div>
                        {enq.email && (
                          <div className="text-slate-400 flex items-center gap-1 truncate max-w-[160px]">
                            <Mail className="w-3 h-3" />
                            {enq.email}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="font-semibold text-slate-200">{enq.courseInterest}</span>
                      </td>
                      <td className="p-4 max-w-xs">
                        <p className="text-slate-400 truncate text-[11px]">
                          {enq.additionalComments || enq.notes || enq.primaryGoal || 'Interested in learning more'}
                        </p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${priorityStyle.bg} ${priorityStyle.color}`}>
                          {enq.priority}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusStyle.bg} ${statusStyle.color}`}>
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="p-4 text-right space-x-2">
                        <a
                          href={`https://wa.me/${enq.phone.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-950/80 hover:bg-emerald-900 text-emerald-400 border border-emerald-800/60 rounded-lg text-xs font-semibold transition-colors"
                          title="WhatsApp Candidate"
                        >
                          <MessageCircle className="w-3 h-3" />
                          Chat
                        </a>

                        <button
                          onClick={() => {
                            setSelectedEnquiry(enq);
                            setEditData({
                              status: enq.status,
                              priority: enq.priority,
                              assignedTo: enq.assignedTo || '',
                              notes: enq.notes || '',
                            });
                            setShowEditModal(true);
                          }}
                          className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold transition-colors"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && selectedEnquiry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-base font-bold text-white font-heading">Manage Enquiry: {selectedEnquiry.fullName}</h3>
                <p className="text-xs text-slate-400">{selectedEnquiry.courseInterest} • {selectedEnquiry.phone}</p>
              </div>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Status</label>
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value as EnquiryStatus })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="converted">Converted</option>
                    <option value="rejected">Closed / Rejected</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Priority</label>
                  <select
                    value={editData.priority}
                    onChange={(e) => setEditData({ ...editData, priority: e.target.value as EnquiryPriority })}
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Counselor Discussion Notes</label>
                <textarea
                  rows={3}
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md shadow-indigo-600/30"
                >
                  {actionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
